import { NewsCardObject } from './types';

export const getImageVariants = (image_url: string, variant: string = 'xs') => {
  if (!image_url) return [image_url];
  // `new URL()` throws on anything that isn't an absolute URL (a bare path,
  // a typo'd scheme, a placeholder string from the backend). This runs
  // during server render for every card on the page, so one malformed
  // thumbnail_url used to take the whole page down with a 500. Treat such
  // values as plain (non-variant) URLs instead and let <img> fail softly.
  let pathname = '';
  try {
    pathname = new URL(image_url).pathname;
  } catch {
    return [image_url];
  }
  const is_bucket_url = pathname.includes('/images/v1/variants');
  if (!is_bucket_url) {
    return [image_url];
  }
  // let sampleUrl = "https://static.inshorts.com/inshorts/images/v1/variants/jpg/m/2025/04_apr/14_mon/img_1744610743752_720.jpg"
  const hasXLVarint = image_url.includes('/xl/');
  const url_parts = image_url.split('/');
  if (url_parts.length < 8) return [image_url]; // Not enough parts to modify

  if (variant) {
    if (variant === 'xl' && !hasXLVarint) {
      variant = 'l'; // If variant is 'xl' but the URL doesn't have it, default to 'l'
    }
    url_parts[8] = variant; // Change the variant part
    return [url_parts.join('/')];
  }
  let variants = ['l', 'm', 's'];
  if (hasXLVarint) {
    variants = ['xl', 'm', 's'];
  }
  return variants.map((variant) => {
    url_parts[8] = variant; // Change the variant part
    return url_parts.join('/');
  });
};

export const parseTitle = (title: string) => {
  if (!title || typeof title !== 'string') return '';
  let string = title.replace(/<|>/gi, '');
  // string = string.replace(/link\/>/ig, "link/");
  while (string.indexOf('nis:link') >= 0 && string.indexOf('nis:link/') >= 0) {
    const hashStart = string.indexOf('nis:link');
    const hashEnd = string.indexOf('nis:link/');
    const nisTypeTag = string.indexOf('nis:type=tag') === -1 ? false : true; // if not tag then it is user
    const valueIndex = string.indexOf('nis:value=');
    const enabledIndex = string.indexOf(' nis:enabled');
    // const nisTypeId = string.indexOf("nis:id="); // Unused for now
    // Check if the nis:type is tag or user
    // let id = "";
    // for (let i = nisTypeId + 7; i < string.length; i++) {
    //     if (string[i] === " ") {
    //         break;
    //     }
    //     id += string[i];
    // }
    if (valueIndex && enabledIndex) {
      let value = string.substring(valueIndex, enabledIndex).split('=')[1];
      const replace_string = string.substring(hashStart, hashEnd + 9);
      value = `${nisTypeTag ? '#' : '@'}${value}`;
      string = string.replace(replace_string, value);
    }
  }
  return string;
};

export const getLocationName = (
  card?: NewsCardObject,
  isComma: boolean = false,
) => {
  if (!card || !card.by_line) {
    return '';
  }
  // let location = card.location_name && card.location_name.length?card.location_name: card.by_line.split("|")[0].split(",")[0];
  const location = '' + card.by_line.split('|')[0].trim();
  if (isComma) {
    return location.split(',')[0];
  }
  return location;
};

export const generateKeywords = (card: NewsCardObject) => {
  // console.log(card);
  if (!card || !card.title) return '';

  const location = card.location_name || getLocationName(card, true);
  const englishLocation = getLocationName(card, true);
  const keywords_from_title = (card.title || '')
    .replace(/\,|\"|\n/gi, '')
    .split(' ')
    .filter((w) => w.length > 4);
  const final_keywords_from_title = sanityOfKeywords(keywords_from_title);

  const content_by = card.data?.uploaded_by_name || '';
  let static_keywords =
    `${location} news,${location} videos,${location} समाचार,daily news,${location},${location} breaking news,breaking news,${location} local news ,local news,${location} लोकल न्यूज,लोकल न्यूज,${location} न्यूज,${content_by},${content_by} news,${content_by} न्यूज`.split(
      ',',
    );
  if (location !== englishLocation) {
    static_keywords =
      `${location} news,${englishLocation} news,${location} videos,${location} समाचार,${englishLocation} समाचार,daily news,${location} latest news,${englishLocation},${location} breaking news,${englishLocation} breaking news,breaking news,${location} local news, ${englishLocation} local news ,local news,${location} लोकल न्यूज,लोकल न्यूज,${location} न्यूज,${content_by},${content_by} news,${content_by} न्यूज`.split(
        ',',
      );
  }
  const keywords = final_keywords_from_title.concat(static_keywords);
  // keywords = Object.keys(keywords.reduce((curr, acc)=>{acc[curr]=1; return acc;}, {}))
  return keywords;
};

export const sanityOfKeywords = (keywords: string[]) => {
  // Remove special characters from keywords
  const sanitizedKeywords: any[] = [];
  if (keywords) {
    keywords.forEach((ke) =>
      sanitizedKeywords.push(ke.replace(/[`~!;:,.<>\{\}\[\]\\\/]/gi, '')),
    );
  }

  // Remove duplicates
  const uniqueSanitizedKeywords = [...new Set(sanitizedKeywords)];

  // Create keywords with and without hastags
  if (uniqueSanitizedKeywords) {
    uniqueSanitizedKeywords.forEach((k) => {
      if (k.includes('#')) {
        uniqueSanitizedKeywords.push(k.replace('#', ''));
      }
    });
  }
  return uniqueSanitizedKeywords;
};

export const getVerifiedImage = (user_verified_value: number) => {
  let user_verified_value_url = 'tick_badge.svg';
  switch (user_verified_value) {
    case 1:
      user_verified_value_url = 'ic_tick_badge_green.svg';
      break;
    case 2:
      user_verified_value_url = 'ic_tick_badge_light_blue.svg';
      break;
    case 3:
      user_verified_value_url = 'ic_tick_badge_grey.svg';
      break;
  }
  return `/_assets/icons/${user_verified_value_url}`;
};

export const formatTimeAgo = (timestamp: string | number) => {
  const date = new Date(timestamp);
  const now = new Date();
  const secondsAgo = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (secondsAgo < 60) {
    return `${secondsAgo} sec ago`;
  } else if (secondsAgo < 3600) {
    const minutesAgo = Math.floor(secondsAgo / 60);
    return `${minutesAgo} min ago`;
  } else if (secondsAgo < 86400) {
    const hoursAgo = Math.floor(secondsAgo / 3600);
    return `${hoursAgo} hrs ago`;
  } else {
    const daysAgo = Math.floor(secondsAgo / 86400);
    return `${daysAgo} days ago`;
  }
};

//   export const generateKeywordsCity = (loc={}) => {
//     const { name, admin_area, sub_admin_area, locality } = loc;

//     const admin_area_keywords = admin_area ? (`news in ${admin_area}, ${admin_area} video news,${admin_area} समाचार,${admin_area},${admin_area} breaking news,${admin_area} local news,${admin_area} लोकल न्यूज,${admin_area} न्यूज`).split(',') : '';
//     const sub_admin_area_keywords = sub_admin_area ? (`news in ${sub_admin_area}, ${sub_admin_area} video news,${sub_admin_area} समाचार,${sub_admin_area},${sub_admin_area} breaking news,${sub_admin_area} local news ,${sub_admin_area} लोकल न्यूज,${sub_admin_area} न्यूज`).split(',') : '';
//     const locality_keywords = locality ? (`news in ${locality}, ${locality} video news,${locality} समाचार,daily news,${locality},${locality} breaking news,${locality} local news ,local news,${locality} लोकल न्यूज,लोकल न्यूज,${locality} न्यूज`).split(',') : '';
//     let keywords = locality_keywords + ',' + sub_admin_area_keywords + ',' + admin_area_keywords;
//     keywords = keywords.split(",").filter(k=>k.length).join(",");
//     return keywords;
// }

// export const generateKeywordsTag = (tag) => {
//   return `#${tag},#${tag} video news,${tag} news,${tag} समाचार,${tag} latest video news,${tag} latest news,${tag} news online,${tag} breaking news,${tag} local video news,${tag} local news,${tag} लोकल न्यूज,${tag} न्यूज`;
// }

export const changeVideoUrlHost = (video_url: string) => {
  if (!video_url) return video_url;
  return video_url.replace(
    /^https?:\/\/(static\.getinpix\.com|static\.inshorts\.com|public-image-cdn\.pix\.in|nis-gs\.pix\.in|nis-profile-cdn\.pix\.in|assets\.inshorts\.com|xcdn\.public\.app)\//,
    'https://static.inshorts.com/',
  );
};

export const getVideoPlaybackUrl = (videoUrl: string) => {
  return changeVideoUrlHost(videoUrl || '');
};

const LANG_MAP: Record<string, string> = {
  HINDI: 'hi',
  ENGLISH: 'en',
  BENGALI: 'bn',
  GUJARATI: 'gu',
  TAMIL: 'ta',
  TELUGU: 'te',
  KANNADA: 'kn',
  MALAYALAM: 'ml',
  MARATHI: 'mr',
  PUNJABI: 'pa',
  ODIA: 'or',
  ORIYA: 'or',
  ASSAMESE: 'as',
  URDU: 'ur',
  SANSKRIT: 'sa',
  SINDHI: 'sd',
  NEPALI: 'ne',
};

const REVERSE_LANG_MAP = Object.fromEntries(
  Object.entries(LANG_MAP).map(([key, value]) => [value, key]),
);

export const shortLang = (lang = 'hi') => {
  const upperLang = lang.toUpperCase();
  return lang.length > 2 ? LANG_MAP[upperLang] || 'hi' : lang.toLowerCase();
};

export const longLang = (lang = 'hi') => {
  const lowerLang = lang.toLowerCase();
  return lang.length === 2
    ? REVERSE_LANG_MAP[lowerLang] || 'HINDI'
    : lang.toUpperCase();
};

// The pipeline stores every share image as PNG (~125KB) and also writes a
// WebP rendition next to it under the same path (~9KB). On a 1.6Mbps mobile
// connection that difference is ~0.7s of LCP, so posters built from a share
// image ask for the WebP. Only the known pipeline URL shape is rewritten;
// anything else is returned unchanged.
export const toWebpVariant = (url: string): string => {
  if (!/\/images\/v1\/variants\/(png|jpe?g)\//.test(url)) return url;
  return url
    .replace(/\/variants\/(png|jpe?g)\//, '/variants/webp/')
    .replace(/\.(png|jpe?g)$/i, '.webp');
};

// Poster for the video player. The page's <link rel="preload"> and the
// <video poster> must come from this one function: if they ever disagree,
// the browser preloads an image the player never uses and the real poster
// still loads late.
//
// Falls back to the share image when thumbnail_url is empty. Cards do ship
// that way (e.g. sp_463cova6rk361, the sample URL for the 121k-page video
// group in Search Console), and with poster="" the player paints nothing
// until the first MP4 frame: 4.6s LCP on throttled mobile versus 1.6s with
// a poster. The feed previews already fall back the same way.
export const getVideoPosterUrl = (
  data?: { thumbnail_url?: string; share_image_url?: string } | null,
): string => {
  if (!data) return '';
  if (data.thumbnail_url) return getImageVariants(data.thumbnail_url, 'l')[0] || '';
  if (data.share_image_url) {
    return getImageVariants(toWebpVariant(data.share_image_url), 'l')[0] || '';
  }
  return '';
};
